<script lang="ts">
  import { onMount } from "svelte";
  import DealCreateSheet from "./components/DealCreateSheet.svelte";
  import FloatingMicButton from "./components/FloatingMicButton.svelte";
  import FloatingTextButton from "./components/FloatingTextButton.svelte";
  import SearchSheet from "./components/SearchSheet.svelte";
  import TextInputSheet from "./components/TextInputSheet.svelte";
  import VoiceConfirmSheet from "./components/VoiceConfirmSheet.svelte";
  import { api, type VoiceParsed } from "./lib/api";
  import { applyTheme, bootstrapUser, getTheme, toggleTheme, type Theme } from "./lib/state.svelte";
  import { isInTelegram } from "./lib/tg";
  import AlertsList from "./routes/AlertsList.svelte";
  import Analytics from "./routes/Analytics.svelte";
  import Calls from "./routes/Calls.svelte";
  import DealCard from "./routes/DealCard.svelte";
  import DealsList from "./routes/DealsList.svelte";
  import Home from "./routes/Home.svelte";
  import InstallerView from "./routes/InstallerView.svelte";
  import ManagerView from "./routes/ManagerView.svelte";
  import MyDealsList from "./routes/MyDealsList.svelte";
  import MyTasksList from "./routes/MyTasksList.svelte";
  import TasksList from "./routes/TasksList.svelte";

  type Route =
    | { name: "home" }
    | { name: "deal"; leadId: number }
    | { name: "manager"; amoUserId: number }
    | { name: "analytics" }
    | { name: "alerts" }
    | { name: "deals"; stage?: string }
    | { name: "tasks"; filter?: string }
    | { name: "my-deals"; sort?: "new" | "silent" | "value" }
    | { name: "my-tasks"; range?: "today" | "today_tomorrow" | "week" | "overdue" | "all" }
    | { name: "installer" }
    | { name: "calls"; filter?: string };

  function parseRoute(): Route {
    const q = new URLSearchParams(location.search);
    const dealQ = q.get("deal");
    if (dealQ && /^\d+$/.test(dealQ)) return { name: "deal", leadId: parseInt(dealQ, 10) };
    const mgrQ = q.get("manager");
    if (mgrQ && /^\d+$/.test(mgrQ)) return { name: "manager", amoUserId: parseInt(mgrQ, 10) };
    const view = q.get("view");
    if (view === "analytics") return { name: "analytics" };
    if (view === "alerts") return { name: "alerts" };
    if (view === "deals") return { name: "deals", stage: q.get("stage") ?? undefined };
    if (view === "tasks") return { name: "tasks", filter: q.get("filter") ?? undefined };
    if (view === "my-deals") {
      const s = q.get("sort");
      const safe = (s === "silent" || s === "value" || s === "new") ? s : "new";
      return { name: "my-deals", sort: safe };
    }
    if (view === "my-tasks") {
      const r = q.get("range");
      const safe = (r === "today" || r === "today_tomorrow" || r === "week" || r === "overdue" || r === "all") ? r : "today_tomorrow";
      return { name: "my-tasks", range: safe };
    }
    if (view === "installer") return { name: "installer" };
    if (view === "calls") return { name: "calls", filter: q.get("filter") ?? undefined };
    return { name: "home" };
  }
  let route = $state<Route>(parseRoute());
  let entryRoute = parseRoute();  // remember initial route so goBack can detect deep-link entries

  function goBack() {
    // If user opened directly into a sub-route (web_app deep link, history is shallow),
    // history.back() would close the Mini App. Detect and go to Home instead.
    const isDeepEntry = entryRoute.name !== "home" && history.length <= 2;
    if (isDeepEntry || history.length <= 1) {
      goHome();
    } else {
      history.back();
    }
  }
  function goHome() {
    history.pushState(null, "", location.pathname);
    route = parseRoute();
  }
  if (typeof window !== "undefined") {
    window.addEventListener("hashchange", () => { route = parseRoute(); });
    window.addEventListener("popstate", () => { route = parseRoute(); });
    // intercept clicks on internal links so we re-render without full reload
    document.addEventListener("click", (ev) => {
      const a = (ev.target as HTMLElement | null)?.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (href.startsWith("?") || href.startsWith("#")) {
        ev.preventDefault();
        history.pushState(null, "", href);
        route = parseRoute();
      }
    });
  }

  let theme = $state<Theme>(getTheme());
  let voiceOpen = $state(false);
  let voiceParsed = $state<VoiceParsed | null>(null);
  let voiceBusy = $state(false);
  let recording = $state(false);
  let toast = $state<string | null>(null);
  let textSheetOpen = $state(false);
  let textBusy = $state(false);
  let textPrefill = $state("");
  let searchOpen = $state(false);
  let dealCreateOpen = $state(false);

  // Global keyboard shortcut: open search via custom event «open-search»
  if (typeof window !== "undefined") {
    window.addEventListener("open-search", () => (searchOpen = true));
    window.addEventListener("open-deal-create", () => (dealCreateOpen = true));
  }

  // MediaRecorder state (not reactive — recorder ref + chunks buffer)
  let recorder: MediaRecorder | null = null;
  let chunks: BlobPart[] = [];
  let stream: MediaStream | null = null;

  // $state so async role update from api.me() triggers re-render across all routes.
  let user = $state(bootstrapUser());

  onMount(async () => {
    applyTheme(theme);
    if (isInTelegram()) {
      try {
        const me = await api.me();
        user.role = me.role;
        user.amoUserAmoId = me.amo_user_amo_id ?? undefined;
      } catch {
        // keep "unknown"
      }
    }
  });

  function switchTheme() {
    theme = toggleTheme();
  }

  function showToast(msg: string, ms = 3500) {
    toast = msg;
    setTimeout(() => { if (toast === msg) toast = null; }, ms);
  }

  async function startRecording() {
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast("Браузер не поддерживает запись микрофона");
      return;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      showToast("Не дали доступ к микрофону");
      console.error(e);
      return;
    }

    chunks = [];
    // Browser/platform mime negotiation:
    //   - Android/desktop: audio/webm;codecs=opus (тоньше, OpenAI Whisper жуёт)
    //   - iOS Safari/TG WebView: webm НЕ поддерживается, fallback на audio/mp4 (AAC),
    //     Whisper тоже принимает m4a/mp4 (https://platform.openai.com/docs/guides/speech-to-text)
    //   - Old fallback: пустой mimeType → браузер выбирает default
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",  // AAC-LC
      "audio/mp4",
      "audio/aac",
    ];
    let mime = "";
    for (const m of candidates) {
      try {
        if (MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      } catch {}
    }
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (e) {
      console.error("MediaRecorder construct failed", mime, e);
      showToast("Запись не поддерживается на этом устройстве");
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
      return;
    }
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };
    recorder.onstop = handleRecorderStop;
    recorder.onerror = (ev) => {
      console.error("recorder.onerror", ev);
      showToast("Ошибка записи: " + ((ev as any).error?.name || "unknown"));
    };
    try {
      recorder.start();
    } catch (e) {
      console.error("recorder.start failed", e);
      showToast("Не удалось начать запись");
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
      recorder = null;
      return;
    }
    recording = true;
  }

  async function stopRecording() {
    if (!recorder) return;
    recording = false;
    voiceBusy = true;
    recorder.stop();
  }

  async function handleRecorderStop() {
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    const recordedMime = recorder?.mimeType || "audio/webm";
    recorder = null;
    if (chunks.length === 0) {
      voiceBusy = false;
      showToast("Запись пустая — попробуй ещё раз");
      return;
    }
    const blob = new Blob(chunks, { type: recordedMime });
    chunks = [];

    // If user is currently viewing a DealCard — attach that deal as voice context
    // so backend NER can auto-link the resulting task/note to this lead.
    const ctx = route.name === "deal" ? { dealId: route.leadId } : undefined;
    try {
      voiceParsed = await api.voiceUpload(blob, ctx);
      voiceOpen = true;
    } catch (e) {
      console.error(e);
      showToast("Ошибка загрузки голоса: " + (e instanceof Error ? e.message : "unknown"));
    } finally {
      voiceBusy = false;
    }
  }

  function onVoicePress() {
    if (voiceBusy) return;
    if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  }

  async function onTextSubmit(text: string) {
    if (textBusy) return;
    textBusy = true;
    const ctx = route.name === "deal" ? { dealId: route.leadId } : undefined;
    try {
      voiceParsed = await api.parseText(text, ctx);
      voiceOpen = true;
    } catch (e) {
      console.error(e);
      showToast("Ошибка: " + (e instanceof Error ? e.message : "unknown"));
    } finally {
      textBusy = false;
    }
  }

  async function onApply() {
    if (!voiceParsed || voiceParsed.command_id === 0) {
      showToast("Команда не сохранена (preview-режим)");
      return;
    }
    try {
      const res = await api.voiceApply(voiceParsed.command_id);
      showToast(res.ok ? "Готово" : "Ошибка");
      // Tell whatever DealCard is mounted to refresh its task list
      if (res.ok) {
        const entityId =
          (voiceParsed.parsed as any)?.entity_amo_id ??
          (route.name === "deal" ? route.leadId : null);
        if (entityId) {
          window.dispatchEvent(new CustomEvent("kvin:deal-changed", { detail: { leadAmoId: entityId } }));
        }
      }
    } catch (e) {
      console.error(e);
      showToast("Ошибка применения: " + (e instanceof Error ? e.message : "unknown"));
    }
  }
</script>

<main>
  {#if route.name === "home"}
    <Home {user} />
  {:else if route.name === "deal"}
    <DealCard leadAmoId={route.leadId} role={user.role} onback={goBack} onhome={goHome} />
  {:else if route.name === "manager"}
    <ManagerView amoUserId={route.amoUserId} onback={goBack} onhome={goHome} />
  {:else if route.name === "analytics"}
    <Analytics onback={goBack} onhome={goHome} />
  {:else if route.name === "alerts"}
    <AlertsList onback={goBack} onhome={goHome} />
  {:else if route.name === "deals"}
    <DealsList stage={route.stage} onback={goBack} onhome={goHome} />
  {:else if route.name === "tasks"}
    <TasksList filter={route.filter} onback={goBack} onhome={goHome} />
  {:else if route.name === "my-deals"}
    <MyDealsList sort={route.sort} onback={goBack} onhome={goHome} />
  {:else if route.name === "my-tasks"}
    <MyTasksList range={route.range} role={user.role} onback={goBack} onhome={goHome} />
  {:else if route.name === "calls"}
    <Calls filter={route.filter} onback={goBack} onhome={goHome} />
  {:else if route.name === "installer"}
    <InstallerView onhome={goHome} />
  {/if}

  <!-- Debug toggle: not part of design, removed when /settings is built -->
  {#if !isInTelegram()}
    <button class="theme-debug" onclick={switchTheme} title="Browser preview only">
      🌓 {theme}
    </button>
  {/if}

  <!-- Mic + text input for director AND manager (Quin handles both intents).
       Hidden only for installer + unknown roles. -->
  {#if user.role === "director" || user.role === "manager"}
    <FloatingMicButton onpress={onVoicePress} {recording} busy={voiceBusy} />
    <FloatingTextButton onpress={() => { textPrefill = ""; textSheetOpen = true; }} busy={textBusy} />
  {/if}

  <TextInputSheet bind:open={textSheetOpen} prefill={textPrefill} onsubmit={onTextSubmit} />
  <SearchSheet bind:open={searchOpen} />
  <DealCreateSheet
    bind:open={dealCreateOpen}
    role={user.role}
    oncreated={(id, name) => {
      history.pushState(null, "", `?deal=${id}`);
      route = parseRoute();
      showToast(`Сделка «${name.slice(0, 40)}» создана`);
    }}
  />

  <VoiceConfirmSheet
    bind:open={voiceOpen}
    parsed={voiceParsed}
    onapply={onApply}
    onedit={() => {
      // Edit = open text sheet with the recognised transcript prefilled,
      // user tweaks wording and re-parses as text (no second mic recording).
      const seed = voiceParsed?.transcript || voiceParsed?.summary || "";
      voiceOpen = false;
      textPrefill = seed;
      setTimeout(() => (textSheetOpen = true), 180);
    }}
    oncancel={() => console.log("[kvin] cancel voice command")}
  />

  {#if toast}
    <div class="toast">{toast}</div>
  {/if}
</main>

<style>
  main {
    max-width: 480px;
    margin: 0 auto;
    min-height: 100vh;
    background: var(--bg);
  }
  .theme-debug {
    position: fixed;
    left: 12px;
    bottom: calc(20px + var(--safe-bottom, 0px));
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--text-mute);
    z-index: 40;
  }
  .toast {
    position: fixed;
    left: 50%;
    top: calc(60px + var(--safe-top, 0px));
    transform: translateX(-50%);
    background: var(--bg-elev);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: var(--shadow-lg);
    z-index: 100;
    animation: toast-in 200ms ease;
    max-width: 88%;
    text-align: center;
  }
  @keyframes toast-in {
    from { transform: translate(-50%, -10px); opacity: 0; }
    to   { transform: translate(-50%, 0);     opacity: 1; }
  }
</style>
